import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { MoreIcon } from '@modernloop/shared/icons';
import { Divider, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';

export enum InterviewGroupOptions {
  MOVE_UP = 'move-up',
  MOVE_DOWN = 'move-down',
  GROUP_WITH_BELOW = 'group-with-below',
  GROUP_WITH_ABOVE = 'group-with-above',
  UNGROUP = 'ungroup',
  RENAME = 'rename-group',
  DELETE = 'delete',
}

type Props = {
  count: number;
  index: number;
  onMenuSelect: (type: InterviewGroupOptions) => void;
};

const InterviewGroupMenu = ({ count, index, onMenuSelect }: Props): JSX.Element => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const hideMenu = () => setShowMenu(false);

  const handleGroupOptionSelect = useCallback(
    (option: InterviewGroupOptions) => {
      onMenuSelect(option);
      hideMenu();
    },
    [onMenuSelect]
  );

  const menuItems = useMemo(() => {
    return [
      index > 0 && (
        <MenuItem
          key={InterviewGroupOptions.MOVE_UP}
          onClick={() => handleGroupOptionSelect(InterviewGroupOptions.MOVE_UP)}
        >
          {t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.moveUp')}
        </MenuItem>
      ),
      index !== count - 1 && (
        <MenuItem
          key={InterviewGroupOptions.MOVE_DOWN}
          onClick={() => handleGroupOptionSelect(InterviewGroupOptions.MOVE_DOWN)}
        >
          {t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.moveDown')}
        </MenuItem>
      ),
      <Divider key="interview-group-menu-divider" />,
      <MenuItem
        key={InterviewGroupOptions.RENAME}
        onClick={() => handleGroupOptionSelect(InterviewGroupOptions.RENAME)}
      >
        {t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.renameGroup')}
      </MenuItem>,
      index !== count - 1 && count !== 1 && (
        <MenuItem
          key={InterviewGroupOptions.GROUP_WITH_BELOW}
          onClick={() => handleGroupOptionSelect(InterviewGroupOptions.GROUP_WITH_BELOW)}
        >
          {t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.groupWithBelow')}
        </MenuItem>
      ),
      index !== 0 && count !== 1 && (
        <MenuItem
          key={InterviewGroupOptions.GROUP_WITH_ABOVE}
          onClick={() => handleGroupOptionSelect(InterviewGroupOptions.GROUP_WITH_ABOVE)}
        >
          {t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.groupWithAbove')}
        </MenuItem>
      ),
      <MenuItem
        key={InterviewGroupOptions.UNGROUP}
        onClick={() => handleGroupOptionSelect(InterviewGroupOptions.UNGROUP)}
      >
        {t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.ungroup')}
      </MenuItem>,
      <Divider key="interview-group-menu-divider" />,
      <MenuItem
        key={InterviewGroupOptions.DELETE}
        onClick={() => handleGroupOptionSelect(InterviewGroupOptions.DELETE)}
      >
        {t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.deleteGroupAndInterviews')}
      </MenuItem>,
    ];
  }, [count, index, handleGroupOptionSelect, t]);

  return (
    <>
      <Tooltip title={t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.moreOptions')}>
        <IconButton ref={moreButtonRef} onClick={() => setShowMenu(!showMenu)}>
          <MoreIcon />
        </IconButton>
      </Tooltip>
      {showMenu && (
        <Menu
          open
          anchorEl={moreButtonRef.current}
          id="interview-group-options"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          disableScrollLock
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onClose={hideMenu}
        >
          {menuItems}
        </Menu>
      )}
    </>
  );
};

export default InterviewGroupMenu;
